/**
 * 城市合伙人合同状态与响应结构统一工具。
 */

export const ORDER_CONTRACT_STATUS_TEXT = {
  WAIT_PAY: '待支付',
  GENERATE_FAILED: '合同生成失败',
  WAIT_REGENERATE: '待重新生成',
  WAIT_OUTBOUND: '待公司邮寄',
  OUTBOUND: '合同已寄出',
  RETURN_MAILED: '用户已寄回',
  PART_RECEIVED: '部分合同已收到',
  RETURNED: '合同退回补签',
  EFFECTIVE: '合同已完成',
  VOID: '合同已作废'
}

export const CONTRACT_RECORD_STATUS_TEXT = {
  WAIT_PAY: '待支付',
  WAIT_REGENERATE: '待重新生成',
  GENERATE_FAILED: '生成失败',
  GENERATED: '待签署寄回',
  RECEIVED: '公司已收到',
  REJECTED: '签章退回',
  VOID: '已作废'
}

/**
 * 读取订单上的城市合伙人合同整体状态。
 * @param {object} order 官网订单
 * @returns {string} 合同状态编码，未携带时返回空字符串
 */
export function orderContractStatus(order) {
  const value = order?.contractStatus
    || order?.cityPartnerContractStatus
    || order?.contractOrderStatus
    || order?.contract?.status
    || order?.contractOrder?.status
    || order?.cityPartnerContract?.status
  return String(value || '').trim().toUpperCase()
}

/**
 * 转换城市合伙人合同整体状态文案。
 * @param {object|string} statusOrOrder 状态编码或官网订单
 * @returns {string} 展示文案
 */
export function orderContractStatusText(statusOrOrder) {
  const status = typeof statusOrOrder === 'object'
    ? orderContractStatus(statusOrOrder)
    : String(statusOrOrder || '').trim().toUpperCase()
  return ORDER_CONTRACT_STATUS_TEXT[status] || (status ? '合同处理中' : '')
}

/**
 * 判断订单是否需要展示城市合伙人合同信息。
 * @param {object} order 官网订单
 * @returns {boolean} 是否城市合伙人订单
 */
export function hasCityPartnerContract(order) {
  return Boolean(orderContractStatus(order))
    || String(order?.productType || '').toUpperCase() === 'CITY_PARTNER_PACKAGE'
    || String(order?.productCode || '').toUpperCase().startsWith('CITY_PARTNER')
}

/**
 * 规范合同详情响应，兼容扁平、嵌套与列表字段命名。
 * @param {object} payload 合同接口响应
 * @returns {object} 面板渲染所需数据
 */
export function normalizeOrderContracts(payload) {
  const data = payload || {}
  const contractOrder = data.contractOrder || data.cityPartnerContractOrder || {}
  const returnAddress = data.contractReturnAddress || data.returnAddress || data.platformReceiveAddress || {}
  const rawRecords = data.records || data.contracts || data.contractRecords || contractOrder.records || []
  const records = (Array.isArray(rawRecords) ? rawRecords : []).map((record, index) => {
    const status = String(record?.contractStatus || record?.recordStatus || record?.status || '').trim().toUpperCase()
    return {
      id: record?.recordId || record?.id,
      contractNo: record?.contractNo || '',
      templateName: record?.templateName || '',
      templateVersion: record?.templateVersion || record?.version || '',
      fileName: record?.fileName || record?.contractFileName || record?.originalFileName || '',
      status,
      statusText: CONTRACT_RECORD_STATUS_TEXT[status] || (status ? '处理中' : '未生成'),
      downloadEnabled: record?.downloadEnabled === true
        || ['GENERATED', 'RECEIVED', 'REJECTED'].includes(status),
      sequence: index + 1
    }
  }).filter(record => record.id)

  return {
    status: orderContractStatus(data) || String(contractOrder.status || '').trim().toUpperCase(),
    statusText: orderContractStatusText(orderContractStatus(data) || contractOrder.status),
    enterpriseName: data.enterpriseName || contractOrder.enterpriseName || '',
    mailingAddress: data.contractMailingAddress || contractOrder.contractMailingAddress || '',
    outboundCourierCompany: data.outboundCourierCompany || contractOrder.outboundCourierCompany || '',
    outboundCourierNo: data.outboundCourierNo || contractOrder.outboundCourierNo || '',
    outboundTime: data.outboundTime || contractOrder.outboundTime || '',
    returnCourierCompany: data.returnCourierCompany || contractOrder.returnCourierCompany || '',
    returnCourierNo: data.returnCourierNo || contractOrder.returnCourierNo || '',
    effectiveTime: data.effectiveTime || contractOrder.effectiveTime || '',
    returnAddress: {
      recipient: returnAddress.recipient || data.contractReturnRecipient || '',
      phone: returnAddress.phone || data.contractReturnPhone || '',
      address: returnAddress.address || data.contractReturnAddress || ''
    },
    instructions: data.contractMailingInstructions || returnAddress.instructions || '',
    records
  }
}

/**
 * 从订单响应回填城市合伙人企业信息。
 * @param {object} order 官网订单
 * @returns {object} 企业信息字段，缺失项为空字符串
 */
export function cityPartnerEnterpriseSnapshot(order) {
  const source = order?.cityPartnerRequest || order?.contract || order?.contractOrder || order || {}
  return {
    enterpriseName: source.enterpriseName || order?.enterpriseName || '',
    usccCode: source.usccCode || order?.usccCode || '',
    legalPersonName: source.legalPersonName || order?.legalPersonName || '',
    contactName: source.contactName || order?.contractContactName || '',
    contactPhone: source.contactPhone || order?.contractContactPhone || '',
    registeredAddress: source.registeredAddress || order?.registeredAddress || '',
    contractMailingAddress: source.contractMailingAddress || order?.contractMailingAddress || ''
  }
}
